/**
 * NICEPAY 결제 모듈 다이나믹 임포트 + 인증창 호출 유틸
 */

export type NicepayParams = Record<string, string | number>;

export interface NicepayOptions {
    popup?: boolean;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    /** NICEPAY 스크립트 URL을 직접 지정하고 싶을 때 (기본: 공식 PG-WEB JS) */
    scriptSrc?: string;
    /** SignData 자동 생성용 가맹점 키 (테스트 MID nicepay00m 기본값 사용) */
    merchantKey?: string;
}

// 가맹점 기본 MerchantKey (환경변수 VITE_SUB_NICEPAY_MERCHANT_KEY)
const DEFAULT_TEST_MERCHANT_KEY = (import.meta.env.VITE_SUB_NICEPAY_MERCHANT_KEY ?? '').trim();

/** YYYYMMDDHHmmss 형식의 EdiDate 생성 */
function buildEdiDate(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
        d.getFullYear().toString() +
        pad(d.getMonth() + 1) +
        pad(d.getDate()) +
        pad(d.getHours()) +
        pad(d.getMinutes()) +
        pad(d.getSeconds())
    );
}

/** SHA-256 hex 다이제스트 (브라우저 SubtleCrypto) */
async function sha256Hex(input: string): Promise<string> {
    const buf = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

const DEFAULT_NICEPAY_SCRIPT_SRC = 'https://pg-web.nicepay.co.kr/v3/common/js/nicepay-pgweb.js';
const NICEPAY_GW_URL = 'https://pg-web.nicepay.co.kr/v3/gwPayment.jsp';

declare global {
    interface Window {
        NICEPAY?: { requestAuth: (params: NicepayParams) => void };
        goPay?: (form: HTMLFormElement) => void;
        nicepaySubmit?: (...args: any[]) => void;
        nicepayClose?: () => void;
    }
}

let nicepayScriptPromise: Promise<void> | null = null;

export function loadNicepayScript(scriptSrc: string = DEFAULT_NICEPAY_SCRIPT_SRC): Promise<void> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('NICEPAY는 브라우저 환경에서만 동작합니다.'));
    }

    if (window.NICEPAY?.requestAuth || typeof window.goPay === 'function') {
        return Promise.resolve();
    }

    if (nicepayScriptPromise) return nicepayScriptPromise;

    nicepayScriptPromise = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${scriptSrc}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('NICEPAY 스크립트 로드 실패')));
            if (window.NICEPAY?.requestAuth || typeof window.goPay === 'function') resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = scriptSrc;
        script.async = true;
        script.charset = 'utf-8';
        script.onload = () => resolve();
        script.onerror = () => {
            nicepayScriptPromise = null;
            reject(new Error('NICEPAY 스크립트 로드 실패'));
        };
        document.head.appendChild(script);
    });

    return nicepayScriptPromise;
}

export async function requestNicepayAuth(params: NicepayParams, options?: NicepayOptions): Promise<void> {
    try {
        if (params.GoodsName && !params.EncGoodsName) {
            params.EncGoodsName = encodeURIComponent(String(params.GoodsName));
        }

        // 결제수단(미지정 시 신용카드 기본)
        if (!params.PayMethod) params.PayMethod = 'CARD';

        // NICEPAY 필수: EdiDate / SignData 자동 생성
        if (!params.EdiDate) {
            params.EdiDate = buildEdiDate();
        }
        if (!params.SignData) {
            const merchantKey = options?.merchantKey || DEFAULT_TEST_MERCHANT_KEY;
            // NICEPAY PG-WEB 공통 산식: SHA256(EdiDate + MID + Amt + MerchantKey)
            // (BILLAUTH/빌키 발급도 동일하게 Amt(=0) 포함)
            const amtStr = String(params.Amt ?? 0);
            params.SignData = await sha256Hex(String(params.EdiDate) + String(params.MID) + amtStr + merchantKey);
        }

        // 통화/문자셋(NICEPAY 공통 기본값)
        if (!params.CharSet) params.CharSet = 'utf-8';
        if (!params.ReplyCharSet) params.ReplyCharSet = 'utf-8';
        if (!params.Currency) params.Currency = 'KRW';
        if (!params.GoodsCnt) params.GoodsCnt = 1;
        if (!params.TransType) params.TransType = 0;
        if (!params.VbankExpDate) params.VbankExpDate = '';

        registerNicepayCallback(options);

        await loadNicepayScript(options?.scriptSrc);

        if (window.NICEPAY?.requestAuth) {
            window.NICEPAY.requestAuth(params);
            return;
        }
        if (typeof window.goPay === 'function') {
            const form = buildNicepayForm(params);
            window.goPay(form);
            return;
        }

        const form = buildNicepayForm(params);
        form.action = NICEPAY_GW_URL;

        if (options?.popup) {
            try {
                const popup = window.open('', 'nicepay_popup', 'width=500,height=600,scrollbars=yes');
                if (popup) form.target = 'nicepay_popup';
            } catch {
                console.warn('Popup blocked, fallback to redirect');
            }
        }

        form.submit();
        setTimeout(() => document.body.contains(form) && document.body.removeChild(form), 1000);
    } catch (error) {
        console.error('NICEPAY auth error:', error);
        options?.onError?.(error);
        throw new Error('결제 인증창을 열 수 없습니다. 다시 시도해주세요.');
    }
}

function registerNicepayCallback(options?: NicepayOptions): void {
    window.nicepaySubmit = function (...args: any[]) {
        try {
            let data: any = null;

            for (const arg of args) {
                if (arg && typeof arg === 'object') {
                    data = arg;
                    break;
                }
                if (typeof arg === 'string') {
                    try {
                        data = JSON.parse(arg);
                        break;
                    } catch {}
                }
            }

            if (!data) {
                const collected: Record<string, string> = {};
                document.querySelectorAll<HTMLInputElement>('input[type="hidden"]').forEach((el) => {
                    if (el.name && el.value) collected[el.name] = el.value;
                });
                if (Object.keys(collected).length) data = collected;
            }

            if (!data) {
                options?.onError?.(new Error('인증 결과 데이터를 받지 못했습니다.'));
                return;
            }

            const code = data.code ?? data.ResultCode ?? data.AuthResultCode;
            const isSuccess = code === '0' || code === '0000';

            if (isSuccess) {
                options?.onSuccess?.(data.result ?? data);
            } else {
                options?.onError?.(new Error(data.message || data.ResultMsg || '인증에 실패했습니다.'));
            }
        } catch (e) {
            options?.onError?.(e);
        } finally {
            cleanupNicepayUiArtifacts();
        }
    };

    window.nicepayClose = function () {
        // 사용자가 결제창을 닫았을 때
    };
}

function cleanupNicepayUiArtifacts(): void {
    try {
        document.querySelectorAll('iframe').forEach((f) => {
            try {
                f.src = 'about:blank';
                f.remove();
            } catch {}
        });
        ['#nice_layer', '#bg_layer', '#np_direct_layer', '.np-direct-layer'].forEach((sel) => {
            document.querySelectorAll(sel).forEach((el) => el.remove());
        });
    } catch {}
}

function buildNicepayForm(params: NicepayParams): HTMLFormElement {
    const form = document.createElement('form');
    form.method = 'POST';
    form.acceptCharset = 'euc-kr';
    form.style.display = 'none';

    Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
    });

    document.body.appendChild(form);
    return form;
}
